import React, { useState, useEffect } from "react";
import "../assets/css/sidebar.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSidebar } from "../contexts/SideBarContext";

const Sidebar = () => {
    const { currentUser } = useAuth();
    const { isSidebarOpen, toggleSidebar } = useSidebar();

    const [collapsedSections, setCollapsedSections] = useState({
        CST: false,
        Components: false,
        Forms: false,
        Tables: false,
        ADS: false,
    });

    // Estado para subsecciones de ADS
    const [collapsedSubsections, setCollapsedSubsections] = useState({
        "chat-ai": false,
        "sponsored-products": false,
        "sponsored-brands": false,
        "sponsored-display": false,
        "competitor-tracking": false,
        "file-analysis": false,
    });

    // Estado para la cuenta activa
    const [selectedAccountName, setSelectedAccountName] = useState('Not selected');
    const [selectedAccountId, setSelectedAccountId] = useState('N/A');

    // Escuchar cambios en localStorage
    useEffect(() => {
        const updateAccountInfo = () => {
            const accountName = localStorage.getItem('selectedAccountName') || 'Not selected';
            const accountId = localStorage.getItem('selectedAccountValue') || 'N/A';
            setSelectedAccountName(accountName);
            setSelectedAccountId(accountId);
        };

        // Actualizar al montar
        updateAccountInfo();

        // Escuchar cambios en localStorage
        window.addEventListener('storage', updateAccountInfo);

        // Escuchar evento personalizado para cambios en la misma pestana
        window.addEventListener('accountChanged', updateAccountInfo);

        return () => {
            window.removeEventListener('storage', updateAccountInfo);
            window.removeEventListener('accountChanged', updateAccountInfo);
        };
    }, []);

    const navigate = useNavigate();

    const handleLogout = () => {
        navigate("/login");
    };

    const toggleCollapse = (section) => {
        setCollapsedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    const toggleSubsection = (subsection) => {
        setCollapsedSubsections((prev) => ({
            ...prev,
            [subsection]: !prev[subsection],
        }));
    };

    // Estructura de subsecciones para ADS
    const adsSubsections = [
        {
            id: "chat-ai",
            icon: "bi bi-chat-dots",
            label: "CHAT AI ASSISTANT",
            items: [
                { label: "Sponsored Products Chat", href: "/ads/sponsored-products", icon: "bi-chat-left-text" },
                { label: "Sponsored Brands Chat", href: "/ads/sponsored-brands", icon: "bi-chat-left-text" },
                { label: "Sponsored Display Chat", href: "/ads/sponsored-display", icon: "bi-chat-left-text" },
            ]
        },
        {
            id: "sponsored-products",
            icon: "bi bi-megaphone",
            label: "SPONSORED PRODUCTS",
            items: [
                { label: "Single keyword campaigns", href: "/ads/sp-single-keyword", icon: "bi-search" },
                { label: "Keyword group campaigns", href: "/ads/sp-keyword-group", icon: "bi-collection" },
                { label: "Product campaigns", href: "/ads/sp-product", icon: "bi-box" },
                { label: "SEO Campaigns", href: "/ads/sp-product-seo", icon: "bi-graph-up" },
                { label: "Category campaigns", href: "/ads/sp-category", icon: "bi-grid" },
                { label: "Brand protection keywords", href: "/ads/sp-brand-keywords", icon: "bi-shield-check" },
                { label: "Brand protection products", href: "/ads/sp-brand-products", icon: "bi-shield-plus" },
                { label: "Auto single target", href: "/ads/sp-auto-single", icon: "bi-target" },
                { label: "Auto multi target", href: "/ads/sp-auto-multi", icon: "bi-bullseye" },
            ]
        },
        {
            id: "sponsored-brands",
            icon: "bi bi-badge-ad",
            label: "SPONSORED BRANDS",
            items: [
                { label: "Group keyword campaigns", href: "/ads/sb-keyword-group", icon: "bi-collection" },
                { label: "Single keyword campaigns", href: "/ads/sb-single-keyword", icon: "bi-search" },
                { label: "Vertical video campaigns", href: "/ads/sb-vertical-video", icon: "bi-camera-video" },
                { label: "Horizontal video campaigns", href: "/ads/sb-horizontal-video", icon: "bi-camera-video-fill" },
                { label: "Product collection campaigns", href: "/ads/sb-product-collection", icon: "bi-images" },
            ]
        },
        {
            id: "sponsored-display",
            icon: "bi bi-display",
            label: "SPONSORED DISPLAY",
            items: [
                { label: "Product campaigns", href: "/ads/sd-product-campaigns", icon: "bi-box" },
                { label: "Audience campaigns", href: "/ads/sd-audience", icon: "bi-people" },
                { label: "Retargeting campaigns", href: "/ads/sd-retargeting", icon: "bi-arrow-repeat" },
            ]
        },
    ];

    const sections = currentUser.userRolesId === 1 || currentUser.userRolesId === 2 || currentUser.userRolesId === 3 ? [
        {
            id: "CST",
            icon: "bi bi-grid",
            label: "CST Segmentation",
            items: [
                { label: "View Reports", href: "/reportes-general", icon: "bi-graph-up" },
                { label: "BlackList", href: "/listas", icon: "bi-slash-circle" },
                { label: "Express Negativizer", href: "/negativizarExpress", icon: "bi-lightning-fill" },
                { label: "Parameters", href: "/parametros", icon: "bi-sliders" },
                { label: "Users", href: "/usuarios", icon: "bi-people" }
            ]
        }
    ] : [
        {
            id: "CST",
            icon: "bi bi-grid",
            label: "CST Segmentation",
            items: [
                { label: "View Reports", href: "/reportes-general", icon: "bi-graph-up" },
                { label: "Express and CST List", href: "/listas", icon: "bi-slash-circle" },
                { label: "Parameters", href: "/parametros", icon: "bi-sliders" }
            ]
        }
    ];

    // Reports & Tracking subsections
    const reportsSubsections = [
        {
            id: "competitor-tracking",
            icon: "bi bi-binoculars",
            label: "COMPETITOR TRACKING",
            items: [
                { label: "Overview", href: "/tracking/overview", icon: "bi-speedometer2" },
                { label: "Pricing Breakdown", href: "/tracking/pricing", icon: "bi-currency-dollar" },
                { label: "Rating Evolution", href: "/tracking/rating", icon: "bi-star" },
                { label: "Ranking Evolution", href: "/tracking/ranking", icon: "bi-bar-chart-line" },
                { label: "Best Sellers Rank", href: "/tracking/best-sellers", icon: "bi-trophy" },
                { label: "Detailed Table", href: "/tracking/detailed", icon: "bi-table" },
                { label: "Web Traffic", href: "/tracking/web-traffic", icon: "bi-globe2" },
                ...(currentUser.userRolesId === 1 || currentUser.userRolesId === 2
                    ? [{ label: "Scheduler Config", href: "/tracking/scheduler-config", icon: "bi-calendar-check" }]
                    : []),
            ]
        },
        {
            id: "file-analysis",
            icon: "bi bi-file-earmark-bar-graph",
            label: "FILE ANALYSIS",
            items: [
                { label: "N-gram Analysis", href: "/analysis/ngram", icon: "bi-search" },
                { label: "Campaigns Evolution", href: "/analysis/campaigns", icon: "bi-graph-up" },
                { label: "Placements SP", href: "/analysis/placements-sp", icon: "bi-geo-alt" },
                { label: "Placements SB", href: "/analysis/placements-sb", icon: "bi-geo" },
                { label: "Parameters Analysis", href: "/analysis/parameters", icon: "bi-sliders" },
                { label: "Sales Estimate", href: "/analysis/sales-estimate", icon: "bi-cash-coin" },
            ]
        },
    ];

    sections.push({
        id: "Reports",
        icon: "bi bi-graph-up-arrow",
        label: "Measurement & Reporting",
        items: [{
            label: "Advanced reports", href: "/reportesCSV"
        }],
        hasSubsections: true,
        subsections: reportsSubsections
    });

    // ADS ahora tiene estructura de subsecciones
    sections.push({
        id: "ADS",
        icon: "bi bi-lightning-charge",
        label: "Ads Assistant",
        showAccountInfo: true,
        accountName: selectedAccountName,
        accountId: selectedAccountId,
        hasSubsections: true,
        subsections: adsSubsections
    });

    return (
        <div>
            <i
                onClick={toggleSidebar}
                aria-label={isSidebarOpen ? "Close side menu" : "Open side menu"}
                className={`icon-sidebar bi ${isSidebarOpen ? "bi-x" : "bi-list"}`}
            ></i>

            <aside
                id="sidebar" className={`border-top border-2 border-dark sidebar ${isSidebarOpen ? "open" : "closed"}`}
            >
                <ul className="sidebar-nav" id="sidebar-nav">
                    {sections.map((section) => (
                        <li className="nav-itemsidebar" key={section.id}>
                            <button className={`nav-link ${collapsedSections[section.id] ? "" : "collapsed"}`}
                                onClick={() => toggleCollapse(section.id)}
                                aria-expanded={collapsedSections[section.id]}
                            >
                                <i className={section.icon}></i>
                                <span>{section.label}</span>
                                <i className={`bi bi-chevron-down fs-5 ms-3 ${collapsedSections[section.id] ? "rotate" : ""
                                        }`}
                                ></i>
                            </button>
                            {collapsedSections[section.id] && (
                                <div>
                                    {/* Mostrar info de cuenta solo en la seccion ADS */}
                                    {section.showAccountInfo && (
                                        <div className="ads-account-info">
                                            <div className="account-label">ACTIVE ACCOUNT</div>
                                            <div className="account-name">{section.accountName}</div>
                                            <div className="account-id">ID: {section.accountId}</div>
                                        </div>
                                    )}

                                    {/* Si tiene subsecciones (como ADS), renderizarlas */}
                                    {section.hasSubsections && section.subsections ? (
                                        <div className="ads-subsections">
                                            {section.subsections.map((subsection) => (
                                                <div className="sidebar-subsection" key={subsection.id}>
                                                    <div className={`sidebar-subsection-header ${collapsedSubsections[subsection.id] ? '' : 'collapsed'}`}
                                                        onClick={() => toggleSubsection(subsection.id)}
                                                    >
                                                        <div className="subsection-title">
                                                            <i className={subsection.icon}></i>
                                                            <span>{subsection.label}</span>
                                                        </div>
                                                        <i className={`bi bi-chevron-down toggle-icon ${collapsedSubsections[subsection.id] ? 'rotate' : ''}`}></i>
                                                    </div>
                                                    <div className={`submenu ${collapsedSubsections[subsection.id] ? '' : 'collapsed'}`}>
                                                        {subsection.items.map((item, index) => (
                                                            <a href={item.href} className="menu-link" key={index}>
                                                                <i className={`bi ${item.icon}`}></i>
                                                                <span>{item.label}</span>
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        /* Renderizado normal para secciones sin subsecciones */
                                        <ul
                                            id={`${section.id.toLowerCase()}-nav`}
                                            className="nav-content"
                                        >
                                            {section.items && section.items.map((item, index) => (
                                                <li key={index}>
                                                    <a href={item.href}>
                                                        <i className={item.icon || ""}></i>
                                                        <span>{item.label}</span>
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>

                {/* Botones inferiores */}
                <div className="sidebar-footer">
                    <button className="sidebar-btn" onClick={handleLogout}>
                        <i className="bi bi-box-arrow-right"></i>
                        <span>Sign Out</span>
                    </button>
                </div>
            </aside>
        </div>
    );
};

export default Sidebar;